import React, { useEffect, useState } from 'react';
import ClayLayout from '@clayui/layout';
import {
    Control,
    Controller,
    FieldErrors,
    FieldValues,
    UseFormWatch,
    UseFormRegister
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { updateCommonValues } from '../../slices/commonSlice';
import { industrySelect } from '../../utils/Config';
import CustomMultiSelect from './CustomMultiSelect';
import {
    getChildTaxonomyCategories,
    getTaxonomyCategories
} from '../../services/taxonomyService';

interface IndustriesProps {
    control: Control<FieldValues, any>;
    watch: UseFormWatch<FieldValues>;
    errors: FieldErrors<FieldValues>;
    setValue: any;
    register: UseFormRegister<FieldValues>;
    onIndustryOptionsChange?: (
        options: { key: number; name: string }[]
    ) => void;
    onSubIndustryOptionsChange?: (
        options: { key: number; name: string }[]
    ) => void;
}

type Item = {
    name: string;
    id: number;
};

interface Option {
    name: string;
    key: string; // key must be string
}

const IndustriesSelect: React.FC<IndustriesProps> = ({
    control,
    errors,
    watch,
    setValue,
    register,
    onIndustryOptionsChange,
    onSubIndustryOptionsChange
}) => {
    const industry = watch('industry');
    const basicDetail = useSelector((state: RootState) => state.common);
    const route = basicDetail.liferayRoute;
    const dispatch = useDispatch();

    const [industryOptions, setIndustryOptions] = useState<
        { key: number; name: string }[]
    >([]);
    const [subIndustryOptions, setSubIndustryOptions] = useState<
        { key: number; name: string }[]
    >([]);
    const [subIndustryByParent, setSubIndustryByParent] = useState<
        Record<string, { key: number; name: string }[]>
    >({});

    const handleTaxonomyCategories = (categories: any) => {
        const transformedOptions =
            categories?.map((item: any) => ({
                key: item.categoryId,
                name: item.name
            })) || [];
        const sortedData = [...transformedOptions].sort((a, b) => {
            if (a.name.trim() === 'Other') return 1;
            if (b.name.trim() === 'Other') return -1;
            return 0;
        });
        setIndustryOptions(sortedData);
        onIndustryOptionsChange?.(transformedOptions);
    };

    useEffect(() => {
        const fetchSubIndustries = async () => {
            if (industry) {
                let industryIds: string[] = Array.isArray(industry)
                    ? industry
                    : [industry];
                if (!industryIds.length && basicDetail?.industry) {
                    industryIds = Array.isArray(basicDetail.industry)
                        ? basicDetail.industry
                        : [basicDetail.industry];
                }
                if (industryIds.length > 0) {
                    try {
                        const missingIds = industryIds.filter(
                            (id) => !subIndustryByParent[id]
                        );

                        let newlyFetchedSubIndustries: Record<
                            string,
                            { key: number; name: string }[]
                        > = {};

                        if (missingIds.length > 0) {
                            const results = await Promise.all(
                                missingIds.map((id) =>
                                    getChildTaxonomyCategories(Number(id))
                                )
                            );

                            results.forEach((data, index) => {
                                const id = missingIds[index];
                                const transformedOptions =
                                    data?.map((item: any) => ({
                                        key: item.categoryId,
                                        name: item.name
                                    })) || [];
                                newlyFetchedSubIndustries[id] =
                                    transformedOptions;

                                if (transformedOptions.length === 0) {
                                    // Removed aggressive Redux clear to prevent state loss during loading
                                }
                            });
                        }

                        // Combine current state with newly fetched data for immediate use
                        const combinedSubIndustryByParent = {
                            ...subIndustryByParent,
                            ...newlyFetchedSubIndustries
                        };

                        setSubIndustryByParent(combinedSubIndustryByParent);

                        const mergedOptions = industryIds.flatMap(
                            (id) => combinedSubIndustryByParent[id] || []
                        );

                        setSubIndustryOptions(mergedOptions);

                        const currentSubIndustry = watch('industrySubcategory');
                        if (currentSubIndustry) {
                            const validKeys = mergedOptions.map((opt) =>
                                opt.key.toString()
                            );
                            const filteredSubIndustry = Array.isArray(
                                currentSubIndustry
                            )
                                ? currentSubIndustry.filter((key) =>
                                      validKeys.includes(key)
                                  )
                                : validKeys.includes(currentSubIndustry)
                                  ? currentSubIndustry
                                  : [];

                            if (
                                JSON.stringify(currentSubIndustry) !==
                                JSON.stringify(filteredSubIndustry)
                            ) {
                                setValue(
                                    'industrySubcategory',
                                    filteredSubIndustry
                                );
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching sub-industries:', error);
                        setSubIndustryOptions([]);
                        setValue('industrySubcategory', []);
                    }
                } else {
                    setSubIndustryOptions([]);
                    setValue('industrySubcategory', []);
                }
            } else {
                setSubIndustryOptions([]);
                setValue('industrySubcategory', []);
            }
        };
        const timeout = setTimeout(fetchSubIndustries, 100);
        return () => clearTimeout(timeout);
    }, [industry, basicDetail.industry]);

    useEffect(() => {
        const currentSubIndustry = watch('industrySubcategory');
        if (currentSubIndustry && subIndustryOptions.length > 0) {
            const validKeys = subIndustryOptions.map((opt) =>
                opt.key.toString()
            );
            const filteredSubIndustry = Array.isArray(currentSubIndustry)
                ? currentSubIndustry.filter((key) => validKeys.includes(key))
                : validKeys.includes(currentSubIndustry)
                  ? currentSubIndustry
                  : [];

            if (
                JSON.stringify(currentSubIndustry) !==
                JSON.stringify(filteredSubIndustry)
            ) {
                setValue('industrySubcategory', filteredSubIndustry);
            }
        }
    }, [subIndustryOptions]);

    useEffect(() => {
        onSubIndustryOptionsChange?.(subIndustryOptions);
    }, [subIndustryOptions, onSubIndustryOptionsChange]);

    useEffect(() => {
        const currentIndustry = watch('industry');
        const currentSubIndustry = watch('industrySubcategory');

        if (currentIndustry && currentSubIndustry) {
            const industryArray = Array.isArray(currentIndustry)
                ? currentIndustry
                : [currentIndustry];

            // Wait until ALL selected industries have their sub-industries loaded in cache
            // to avoid clearing the selection prematurely on mount.
            const allLoaded = industryArray.every(
                (id) => subIndustryByParent[id] !== undefined
            );
            if (!allLoaded) return;

            const subIndustryArray = Array.isArray(currentSubIndustry)
                ? currentSubIndustry
                : [currentSubIndustry];

            const validSubIndustries = industryArray.flatMap(
                (industryId) =>
                    subIndustryByParent[industryId]?.map((sub) =>
                        sub.key.toString()
                    ) || []
            );

            const filteredSubIndustry = subIndustryArray.filter((key) =>
                validSubIndustries.includes(key)
            );

            if (
                JSON.stringify(currentSubIndustry) !==
                JSON.stringify(filteredSubIndustry)
            ) {
                setValue('industrySubcategory', filteredSubIndustry);
            }
        }
    }, [industry]);

    useEffect(() => {
        if (route) {
            getTaxonomyCategories(
                (window as any)?.Liferay?.ThemeDisplay?.getScopeGroupId(),
                basicDetail.liferayRoute == '/' ||
                    basicDetail.liferayRoute == '/pitch-form'
                    ? 'Company Database Sector'
                    : 'Industry'
            ).then((categories: any) => {
                handleTaxonomyCategories(categories);
            });
        }
    }, [route]);

    useEffect(() => {
        if (subIndustryOptions?.length === 1) {
            const id = subIndustryOptions.map((item) => item.key.toString());
            setValue('industrySubcategory', id, {
                shouldValidate: true,
                shouldDirty: true
            });
        } else if (subIndustryOptions?.length > 1) {
            const currentSubIndustry = watch('industrySubcategory');
            const notApplicableItems = subIndustryOptions.filter(
                (item) => item.name.trim().toLowerCase() === 'not applicable'
            );
            const notApplicableIds = notApplicableItems.map((item) =>
                item.key.toString()
            );
            const otherItems = subIndustryOptions.filter(
                (item) => item.name.trim().toLowerCase() !== 'not applicable'
            );

            if (otherItems.length > 0) {
                if (currentSubIndustry) {
                    const filtered = Array.isArray(currentSubIndustry)
                        ? currentSubIndustry.filter(
                              (id) => !notApplicableIds.includes(id)
                          )
                        : notApplicableIds.includes(currentSubIndustry)
                          ? []
                          : currentSubIndustry;

                    if (
                        JSON.stringify(currentSubIndustry) !==
                        JSON.stringify(filtered)
                    ) {
                        setValue('industrySubcategory', filtered, {
                            shouldValidate: true,
                            shouldDirty: true
                        });
                    }
                }
            } else {
                if (notApplicableItems.length > 0) {
                    const id = [notApplicableItems[0].key.toString()];
                    setValue('industrySubcategory', id, {
                        shouldValidate: true,
                        shouldDirty: true
                    });
                }
            }
        }
    }, [industry, subIndustryOptions, setValue, watch]);

    return (
        <>
            {industrySelect.map((input) => {
                if (
                    input.name === 'industrySubcategory' &&
                    subIndustryOptions.length === 0
                ) {
                    return null;
                }
                let options: Option[] = [];
                let objData: Record<string, string> = {};
                if (input?.name === 'industry') {
                    industryOptions?.forEach((item) => {
                        objData[item.key.toString()] = item.name;
                    });
                    options = industryOptions?.map((item) => ({
                        name: item.name,
                        key: item.key.toString()
                    }));
                } else if (
                    input?.name === 'industrySubcategory' &&
                    subIndustryOptions?.length > 0
                ) {
                    let filteredOptions = subIndustryOptions;
                    if (subIndustryOptions.length > 1) {
                        const notApplicableItems = subIndustryOptions.filter(
                            (item) =>
                                item.name.trim().toLowerCase() ===
                                'not applicable'
                        );
                        const otherItems = subIndustryOptions.filter(
                            (item) =>
                                item.name.trim().toLowerCase() !==
                                'not applicable'
                        );

                        if (otherItems.length > 0) {
                            filteredOptions = otherItems;
                        } else if (notApplicableItems.length > 1) {
                            filteredOptions = [notApplicableItems[0]];
                        }
                    }

                    filteredOptions.forEach((item) => {
                        objData[item.key.toString()] = item.name;
                    });

                    options = filteredOptions.map((item) => ({
                        name: item.name,
                        key: item.key.toString()
                    }));
                }
                return (
                    <ClayLayout.Col
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                        className=""
                        key={input.name}
                    >
                        <Controller
                            name={input.name}
                            control={control}
                            defaultValue={
                                (basicDetail as any)[input.name] || []
                            }
                            rules={{
                                required:
                                    (input.name == 'industrySubcategory'
                                        ? subIndustryOptions.length > 0
                                        : true) &&
                                    `${input.label.replaceAll('*', '')} is required`
                            }}
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <CustomMultiSelect
                                        value={
                                            Array.isArray(value)
                                                ? value
                                                : value
                                                  ? [value]
                                                  : []
                                        }
                                        handleOnChange={onChange}
                                        key={'mySelectId' + input.name}
                                        limit={options.length + 1}
                                        register={register}
                                        required={true}
                                        errors={errors}
                                        options={options}
                                        name={input.name}
                                        placeholder={input.label}
                                        optionListObj={objData}
                                    />
                                );
                            }}
                        />
                    </ClayLayout.Col>
                );
            })}
        </>
    );
};

export default IndustriesSelect;
